import { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import { Close, Info, Send } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import SecondaryButton from "../buttons/SecondaryButton";
import { Guest } from "src/types";
import { useSnackbar } from "src/hooks";
import useResendWebinarPromotionMessage from "src/mutations/useResendWebinarPromotionMessage";
import { GuestStatusIndicator } from "src/componentsV2/GuestStatusIndicator";

export function ResendPromotionModal({
  open,
  onClose,
  eventId,
  guests,
}: {
  open: boolean;
  onClose: () => void;
  eventId: number;
  guests: Guest[];
}) {
  // eligible: staging + if promotionSender assigned then tokenStatus must be current
  const eligibleGuests = useMemo(
    () =>
      (guests || []).filter(
        (g) =>
          g.status === "staging" &&
          (!g.promotionSender || g.promotionSender?.tokenStatus === "current"),
      ),
    [guests],
  );

  const [selectedGuestIds, setSelectedGuestIds] = useState<Set<number>>(
    new Set(eligibleGuests.map((g) => g.id)),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [openSnackbar] = useSnackbar();
  const resendWebinarPromotionMessage = useResendWebinarPromotionMessage();

  const handleGuestToggle = (id: number) => {
    const next = new Set(selectedGuestIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedGuestIds(next);
  };

  const selectedCount = selectedGuestIds.size;
  const allSelected =
    eligibleGuests.length > 0 &&
    eligibleGuests.every((g) => selectedGuestIds.has(g.id));
  const someSelected =
    selectedCount > 0 && selectedCount < eligibleGuests.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedGuestIds(new Set());
    } else {
      setSelectedGuestIds(new Set(eligibleGuests.map((g) => g.id)));
    }
  };

  const handleSubmit = async () => {
    if (selectedGuestIds.size === 0) {
      setError("Please select at least one guest");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await resendWebinarPromotionMessage({
        eventId,
        contactIds: Array.from(selectedGuestIds),
      });
      openSnackbar("Promotion message resend initiated");
      onClose();
    } catch (e: any) {
      const message =
        e?.response?.body?.message ||
        e?.response?.body?.error ||
        e?.message ||
        "Failed to resend";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: "hidden", padding: 0 },
      }}
    >
      <Box
        sx={{
          backgroundColor: "#1D4971",
          color: "white",
          py: 2.5,
          px: 0,
          textAlign: "center",
          position: "relative",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Resend Promotion Message
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            color: "white",
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, "&.MuiDialogContent-root": { padding: 0 } }}>
        <Stack spacing={0}>
          <Alert severity="info" icon={<Info />} sx={{ m: 3, mb: 2 }}>
            Review the guests below and select the ones you want to resend the
            promotion message to.
          </Alert>

          {/* Header */}
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              borderTop: 1,
              borderBottom: 1,
              borderColor: "divider",
              py: 2,
              px: 3,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={1}></Grid>
              <Grid item xs={4}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Guest
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Event Guest Status
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Promotion Sender
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Select all */}
          <Box sx={{ px: 3, py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAll}
              />
              <Typography variant="subtitle2">
                Select All ({eligibleGuests.length} guests)
              </Typography>
              <Chip
                label={`${selectedCount} selected`}
                color={selectedCount > 0 ? "primary" : "default"}
                size="small"
              />
            </Stack>
          </Box>

          {/* Rows */}
          <Box sx={{ maxHeight: 400, overflow: "auto" }}>
            {eligibleGuests.map((guest) => {
              const isSelected = selectedGuestIds.has(guest.id);
              return (
                <Box
                  key={guest.id}
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    py: 2,
                    px: 3,
                    cursor: "pointer",
                    backgroundColor: isSelected ? "primary.50" : "transparent",
                    "&:hover": { backgroundColor: "grey.50" },
                  }}
                  onClick={() => handleGuestToggle(guest.id)}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={1}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleGuestToggle(guest.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Stack>
                        <Typography variant="body2" fontWeight="medium">
                          {guest.firstName} {guest.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {guest.email}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={3}>
                      <GuestStatusIndicator status={guest.status} />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2">
                        {guest.promotionSender
                          ? `${guest.promotionSender.firstName} ${guest.promotionSender.lastName}`
                          : "Organizer"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Box>

          {error && (
            <Alert severity="error" sx={{ m: 3, mt: 2 }}>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
        <SecondaryButton onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          startIcon={<Send />}
          disabled={selectedCount === 0}
        >
          {`Resend Promotion Message (${selectedCount})`}
        </LoadingButton>
      </DialogActions>
    </MuiDialog>
  );
}

export default ResendPromotionModal;
